import React from 'react';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import Tooltip from '@amzn/meridian/tooltip';
import { useTranslation } from 'src/arb-wrapper';
import {
  type SyncTargetErrorType,
  SyncTargetErrorTypes,
  type SyncTargetStatus,
  SyncTargetStatuses,
} from 'src/types/sync-target.types';
import { formatUtcDateTimeWithZone } from 'src/utils/formatDateTime';
import { EMPTY_TABLE_VALUE } from 'src/constants';

const STATUS_TAG_TYPES: Partial<Record<SyncTargetStatus, TagType>> = {
  [SyncTargetStatuses.ACTIVE]: 'success',
  [SyncTargetStatuses.PAUSED]: 'neutral',
  [SyncTargetStatuses.ERROR]: 'error',
  [SyncTargetStatuses.QUEUED]: 'warning',
  [SyncTargetStatuses.IN_PROGRESS]: 'warning',
};

/**
 * Bender's raw statuses are mapped to labels rather than shown verbatim.
 *
 * Typed `Partial` on purpose: these keys mirror Bender's enums by hand (see the type
 * file header), so a value Bender adds later would arrive at runtime without being in
 * this map. Every lookup therefore falls back rather than rendering `undefined`.
 */
const STATUS_LABEL_KEYS: Partial<Record<SyncTargetStatus, string>> = {
  [SyncTargetStatuses.ACTIVE]: 'box_folder_status_active',
  [SyncTargetStatuses.PAUSED]: 'box_folder_status_paused',
  [SyncTargetStatuses.ERROR]: 'box_folder_status_error',
  [SyncTargetStatuses.QUEUED]: 'box_folder_status_queued',
  [SyncTargetStatuses.IN_PROGRESS]: 'box_folder_status_in_progress',
};

const ERROR_TYPE_KEYS: Partial<Record<SyncTargetErrorType, string>> = {
  [SyncTargetErrorTypes.ACCESS_LOST]: 'box_folder_error_access_lost',
  [SyncTargetErrorTypes.SCHEDULE_PROVISIONING_FAILED]: 'box_folder_error_setup_failed',
  [SyncTargetErrorTypes.ENUMERATION_FAILED]: 'box_folder_error_sync_failed',
};

interface BoxFolderStatusTagProps {
  status: SyncTargetStatus;
  errorType?: SyncTargetErrorType;
  errorAt?: string;
  testId?: string;
}

/**
 * Renders an enrolled Box folder's sync status, with the failure reason in a tooltip.
 *
 * The reason is shown **only** when `status === 'ERROR'`, never merely because
 * `errorType` is present. Bender deliberately retains `errorType` and `errorAt`
 * through a sync and through a pause, so keying the tooltip off field presence would
 * surface a long-resolved failure forever. There is intentionally no prop to opt into
 * presence-based rendering.
 */
export const BoxFolderStatusTag: React.FC<BoxFolderStatusTagProps> = ({
  status,
  errorType,
  errorAt,
  testId,
}) => {
  const { t } = useTranslation();

  const tag = (
    <Tag data-testid={testId} type={STATUS_TAG_TYPES[status] ?? 'neutral'}>
      {t(STATUS_LABEL_KEYS[status] ?? 'box_folder_status_unknown')}
    </Tag>
  );

  if (status !== SyncTargetStatuses.ERROR) {
    return tag;
  }

  // Falls back on a lookup miss as well as on an absent errorType: an errorType
  // Bender adds after this map was written would otherwise render t(undefined).
  const reason = t((errorType && ERROR_TYPE_KEYS[errorType]) ?? 'box_folder_error_generic');
  const failedAt = t('box_folder_error_at', {
    errorAt: formatUtcDateTimeWithZone(errorAt, EMPTY_TABLE_VALUE),
  });

  return (
    <Tooltip position="top" title={`${reason} ${failedAt}`}>
      <div data-testid={testId ? `${testId}-error-tooltip` : undefined} style={{ display: 'inline-flex' }}>
        {tag}
      </div>
    </Tooltip>
  );
};
